import React, { useState, useEffect, useRef } from 'react';
import { X, Bell, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SignalNotificationPopup = ({ signalDetails, onClose, onDismiss }) => {
  const [countdown, setCountdown] = useState(signalDetails?.execute_countdown || 0);
  const [isVisible, setIsVisible] = useState(true);
  const [soundPlayed, setSoundPlayed] = useState(false);
  const audioContextRef = useRef(null);

  // LOUD ALERT SOUND - Plays when popup appears
  const playAlertSound = () => {
    if (soundPlayed) return;
    
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      
      const isBuy = signalDetails?.signal === 'BUY';
      
      // Play a series of loud beeps
      const playBeep = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'square'; // Louder than sine
        
        // LOUD volume
        gainNode.gain.setValueAtTime(0.5, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      
      if (isBuy) {
        // BUY signal - ascending tones (exciting!)
        playBeep(600, now, 0.15);
        playBeep(800, now + 0.2, 0.15);
        playBeep(1000, now + 0.4, 0.15);
        playBeep(1200, now + 0.6, 0.3);
        // Repeat
        playBeep(600, now + 1.0, 0.15);
        playBeep(800, now + 1.2, 0.15);
        playBeep(1000, now + 1.4, 0.15);
        playBeep(1200, now + 1.6, 0.3);
      } else {
        // SELL signal - descending tones (urgent!)
        playBeep(1000, now, 0.15);
        playBeep(800, now + 0.2, 0.15);
        playBeep(600, now + 0.4, 0.15);
        playBeep(400, now + 0.6, 0.3);
        // Repeat
        playBeep(1000, now + 1.0, 0.15);
        playBeep(800, now + 1.2, 0.15);
        playBeep(600, now + 1.4, 0.15);
        playBeep(400, now + 1.6, 0.3);
      }
      
      setSoundPlayed(true);
      console.log('🔊 Alert sound played!');
      
    } catch (e) {
      console.error('Audio error:', e);
    }
    
    // Vibrate if on mobile
    if (navigator.vibrate) {
      navigator.vibrate([300, 100, 300, 100, 300]);
    }
  };

  // Play sound when component mounts
  useEffect(() => {
    playAlertSound();
    
    return () => {
      // Cleanup audio context
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Countdown timer - AUTO CLOSE when reaches 0
  useEffect(() => {
    if (countdown <= 0) {
      // Auto-close popup when countdown reaches 0
      console.log('⏱️ Countdown reached 0 - auto-closing popup');
      setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, 3000); // Give 3 seconds to see "EXECUTE NOW!" before closing
      return;
    }
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [countdown, onClose]);

  // Update countdown from props
  useEffect(() => {
    if (signalDetails?.execute_countdown !== undefined && signalDetails.execute_countdown > 0) {
      setCountdown(signalDetails.execute_countdown);
    }
  }, [signalDetails?.execute_countdown]);

  if (!signalDetails || !isVisible) return null;

  const isBuy = signalDetails.signal === 'BUY';
  const bgColor = isBuy ? 'bg-green-600' : 'bg-red-600';

  const formatCountdown = (seconds) => {
    if (seconds <= 0) return '🔥 EXECUTE NOW!';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  // Build the message exactly like Telegram format
  const signalEmoji = isBuy ? '🟢' : '🔴';
  const directionEmoji = isBuy ? '📈' : '📉';
  const strengthEmoji = signalDetails.strength || '✅';
  const assetType = signalDetails.asset_type || '📊';
  const payoutText = signalDetails.payout > 0 
    ? `${signalDetails.payout >= 92 ? '🔥' : signalDetails.payout >= 80 ? '💰' : '💵'} Payout: ${signalDetails.payout}%`
    : '🔥 High payout';
  
  // Accuracy and expiry
  const accuracy = signalDetails.accuracy || 70;
  const expiry = signalDetails.expiry || '1 min';
  const signalReason = signalDetails.signal_reason || signalDetails.reason || `High Potential ${signalDetails.signal}`;
  const accuracyColor = accuracy >= 85 ? 'text-green-300' : accuracy >= 75 ? 'text-yellow-300' : 'text-white';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className={`relative w-full max-w-lg ${bgColor} rounded-2xl shadow-2xl overflow-hidden animate-pulse`} style={{animation: 'pulse 1s ease-in-out 3'}}>
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors z-10"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Header with bell icon */}
        <div className="bg-black/30 px-6 py-4 flex items-center justify-center gap-3">
          <Bell className="w-10 h-10 text-yellow-300 animate-bounce" />
          <Volume2 className="w-8 h-8 text-yellow-300 animate-pulse" />
          <span className="text-3xl font-bold text-white">🚨 NEW SIGNAL! 🚨</span>
          <Volume2 className="w-8 h-8 text-yellow-300 animate-pulse" />
          <Bell className="w-10 h-10 text-yellow-300 animate-bounce" />
        </div>

        {/* Message Content - SAME FORMAT AS TELEGRAM */}
        <div className="p-6 space-y-4">
          {/* Signal Header */}
          <div className="text-center">
            <div className="text-5xl font-bold text-white mb-2 animate-pulse">
              {signalEmoji} {signalDetails.signal} SIGNAL! {strengthEmoji}
            </div>
          </div>
          
          {/* Accuracy and Expiry - PROMINENT */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/20 rounded-lg p-3 text-center border-2 border-white/30">
              <div className="text-sm text-white/80">🎯 Accuracy</div>
              <div className={`text-3xl font-bold ${accuracyColor}`}>{accuracy}%</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center border-2 border-white/30">
              <div className="text-sm text-white/80">⏱️ Expiry</div>
              <div className="text-3xl font-bold text-white">{expiry}</div>
            </div>
          </div>

          {/* Asset Info Box */}
          <div className="bg-white/10 rounded-xl p-4 space-y-2">
            <div className="text-2xl font-bold text-white">
              📊 {signalDetails.asset_name}
            </div>
            <div className="text-white/90 font-mono text-xl">
              🏷 {signalDetails.asset_symbol}
            </div>
            <div className="text-white/90 text-lg">
              💼 {assetType}
            </div>
            <div className="text-white font-bold text-xl">
              {payoutText}
            </div>
            <div className="text-3xl font-bold text-white">
              {directionEmoji} {signalDetails.signal}
            </div>
          </div>

          {/* Signal Reason with Indicator Source */}
          <div className="text-center text-xl text-white font-bold bg-white/10 rounded-lg p-3">
            🎯 {signalReason}
          </div>

          {/* Execution Time Box */}
          <div className="bg-yellow-500/40 rounded-xl p-4 text-center border-4 border-yellow-400">
            <div className="text-white text-xl mb-2">
              ⏰ Execute at NEXT candle formation
            </div>
            <div className="text-white text-lg">
              📈 Timeframe: <span className="font-bold">{signalDetails.timeframe_name}</span>
            </div>
            <div className="text-white text-lg">
              🕐 Open trade at: <span className="font-bold">{signalDetails.execute_time_est} EST</span>
            </div>
            
            {/* Countdown Timer - BIG AND PROMINENT */}
            <div className={`mt-4 text-6xl font-bold ${countdown <= 10 ? 'text-yellow-300 animate-pulse' : 'text-white'}`}>
              ⏱️ {formatCountdown(countdown)}
            </div>
            
            {/* Auto-close notice */}
            {countdown <= 0 && (
              <div className="mt-2 text-yellow-200 text-sm animate-pulse">
                Popup will close in 3 seconds...
              </div>
            )}
          </div>

          {/* Action Instruction */}
          <div className="bg-white/20 rounded-xl p-4 text-center border-2 border-white/50">
            <p className="text-white text-2xl font-bold">
              🎯 Open Pocket Option and place {signalDetails.signal} on {signalDetails.asset_symbol?.replace('_', ' ')}
            </p>
          </div>

          {/* Footer */}
          <div className="text-center text-white/80 text-lg">
            🤖 Bot tracking with Martingale
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <Button 
            onClick={handleClose}
            className="flex-1 bg-white text-gray-900 hover:bg-white/90 font-bold text-xl py-8"
          >
            ✓ GOT IT - PLACING TRADE
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignalNotificationPopup;
